import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {useObserveViewport} from '../../hooks';
import {useFetchEntityUsers} from '../../hooks/use-fetch-entity-users';
import {translations} from '../../i18n/translations';
import {AutocompleteInput, Option} from '../role-autocomplete/autocomplete-input';

type UsersInputProps = {
  onChange: (user: AffiliatedUser | undefined) => void;
};

/**
 * Users filtering input. Displays ALL ACTIVE users in the entity.
 */
export function UsersInput(props: UsersInputProps) {
  const usersData = useFetchEntityUsers({activeUsers: true});

  const options = useMemo(() => {
    if (!usersData.items) return [];

    return usersData.items.map(({user}) => ({
      key: user.id!,
      label: user.displayName || user.name,
      picture: user.picture || '',
    }));
  }, [usersData.items]);

  return (
    <BaseUsersInput
      options={options}
      loading={usersData.isLoading}
      defaultOptionLabel={translations.planner.header.filter_all_seniors_label}
      onChange={(userId) => {
        const user = usersData.items?.find(({user}) => user.id === userId);
        props.onChange(user);
      }}
    />
  );
}

// ---------------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------------

/**
 * Base users input component. No data fetching logic & context. Only displays the UI.
 */
export function BaseUsersInput(props: {
  options: Option[];
  loading: boolean;
  defaultOptionLabel?: string;
  onChange: (userId: string | undefined) => void;
}) {
  const defaultLabel = props.defaultOptionLabel ?? translations.quick_access_login.all_users;
  const {t} = useTranslation();
  const {isRunningCloseToMobile} = useObserveViewport();
  const [selectedOption, setSelectedOption] = useState<Option>({
    key: '',
    label: t(defaultLabel),
    picture: '',
  });

  return (
    <AutocompleteInput
      virtualized
      sx={{
        width: isRunningCloseToMobile ? 155 : 200,
      }}
      options={props.options}
      selectedOption={selectedOption}
      loading={props.loading}
      showEmptyOption
      emptyOptionLabel={t(defaultLabel)}
      onChange={(option) => {
        setSelectedOption(option);
        props.onChange(option.key);
      }}
    />
  );
}
